import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { MapPinIcon, PhotoIcon, XMarkIcon } from "@heroicons/react/20/solid";
import EventAPI from "../api/EventApi";
import { GlobalContext } from "../context/GlobalContext";
import { useParams } from "react-router-dom";
import { collection, query, orderBy, onSnapshot, where } from "firebase/firestore";
import { db } from "../firebase";
import useImageResizer from "../utils/useImageResizer";
import { uploadChatImage } from "../utils/chatImageUpload";
import ChatMessage from "./ChatMessage";
import LocationPickerModal from "./LocationPickerModal";

export const Chat = ({ title, chatData: propChatData, subCollectionName, parentID: propParentID, parentCollection = "events", onSend, showInput = true, isPrivate = false, requireCheckIn = true }) => {
  const [message, setMessage] = useState("");
  const [sendType, setSendType] = useState("chat"); // "chat" or "notify"
  const [notifyTarget, setNotifyTarget] = useState(requireCheckIn ? "checked-in" : "everyone"); // "checked-in" or "everyone"
  const { globalState } = useContext(GlobalContext);
  const { eventID } = useParams();
  const [currentUserID, setCurrentUserID] = useState("")

  const [messages, setMessages] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const containerRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Send state
  const [isSending, setIsSending] = useState(false);


  // Image upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // { file, previewUrl }
  const { setImageForResize } = useImageResizer(1200, 1200, 0.85);

  // Reaction state
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState(null);
  const REACTION_EMOJIS = ['❤️', '👍', '👎', '😂', '‼️', '❓'];

  // Location picker state
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isSendingLocation, setIsSendingLocation] = useState(false);

  const parentID = propParentID || eventID;

  // Initial fetch and real-time listener for NEW messages
  useEffect(() => {
    if (!parentID || !subCollectionName) return;

    let isMounted = true;

    const loadInitialMessages = async () => {
      try {
        const { messages: initialMessages, lastDoc, hasMore: more } = await EventAPI.getPaginatedMessages(
          parentCollection,
          parentID,
          subCollectionName,
          25
        );

        if (isMounted) {
          // Sort ascending for display
          const sorted = [...initialMessages].sort((a, b) => a.timestamp - b.timestamp);
          setMessages(sorted);
          setLastVisible(lastDoc);
          setHasMore(more);
          setIsInitialLoad(false);

          // Scroll to bottom after initial load
          setTimeout(() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
          }, 100);
        }
      } catch (error) {
        console.error("Error loading initial messages:", error);
      }
    };

    loadInitialMessages();

    // Listener for NEW messages (those arriving after now)
    const subCollectionRef = collection(db, parentCollection, parentID, subCollectionName);
    const now = Date.now();
    const q = query(
      subCollectionRef,
      where("timestamp", ">", new Date(now)),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) return;

      const newMessages = snapshot.docChanges()
        .filter(change => change.type === "added")
        .map(change => {
          const data = change.doc.data();
          const ts = data.timestamp && typeof data.timestamp.toMillis === "function"
            ? data.timestamp.toMillis()
            : (typeof data.timestamp === "number" ? data.timestamp : Date.now());
          return { id: change.doc.id, ...data, timestamp: ts };
        });

      if (newMessages.length > 0) {
        setMessages(prev => {
          // Avoid duplicates
          const existingIds = new Set(prev.map(m => m.id));
          const filteredNew = newMessages.filter(m => !existingIds.has(m.id));
          return [...prev, ...filteredNew].sort((a, b) => a.timestamp - b.timestamp);
        });

        // Scroll to bottom on new message
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
          }
        }, 100);
      }
    });

    // Listener for modifications (reactions, soft-deletes) and removals (permanent deletes)
    const allMessagesRef = collection(db, parentCollection, parentID, subCollectionName);
    const unsubscribeModified = onSnapshot(allMessagesRef, (snapshot) => {
      const changes = snapshot.docChanges();
      const modified = changes.filter(c => c.type === "modified");
      const removed = changes.filter(c => c.type === "removed");
      if (modified.length === 0 && removed.length === 0) return;

      setMessages(prev => {
        const removedIds = new Set(removed.map(c => c.doc.id));
        return prev
          .filter(msg => !removedIds.has(msg.id))
          .map(msg => {
            const change = modified.find(c => c.doc.id === msg.id);
            if (change) {
              const data = change.doc.data();
              return { ...msg, reactions: data.reactions || {}, deleted: data.deleted ?? false };
            }
            return msg;
          });
      });
    });

    return () => {
      isMounted = false;
      unsubscribe();
      unsubscribeModified();
    };
  }, [parentID, subCollectionName, parentCollection]);

  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || !hasMore || !lastVisible) return;

    setLoadingMore(true);
    // Save current scroll height to adjust later
    if (containerRef.current) {
      scrollHeightRef.current = containerRef.current.scrollHeight;
    }

    try {
      const { messages: olderMessages, lastDoc, hasMore: more } = await EventAPI.getPaginatedMessages(
        parentCollection,
        parentID,
        subCollectionName,
        25,
        lastVisible
      );

      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const filteredOlder = olderMessages.filter(m => !existingIds.has(m.id));
        return [...filteredOlder, ...prev].sort((a, b) => a.timestamp - b.timestamp);
      });
      setLastVisible(lastDoc);
      setHasMore(more);

      // Adjust scroll position to prevent jumping
      setTimeout(() => {
        if (containerRef.current) {
          const newScrollHeight = containerRef.current.scrollHeight;
          containerRef.current.scrollTop = newScrollHeight - scrollHeightRef.current;
        }
      }, 0);

    } catch (error) {
      console.error("Error loading more messages:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, lastVisible, parentID, subCollectionName, parentCollection]);

  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && !loadingMore && hasMore) {
      loadMoreMessages();
    }
  };

  const handleSendMessage = async () => {
    if (message.trim() === "" || isSending) return;

    setIsSending(true);
    try {
      const userNameToUse = globalState?.user?.name || globalState?.vendor?.name || "Admin";
      const type = sendType === "notify" ? "notify" : null;

      if (typeof onSend === "function") {
        await onSend(message, currentUserID, userNameToUse, type, notifyTarget);
      } else {
        await EventAPI.sendMessage(
          parentID,
          subCollectionName,
          message,
          currentUserID,
          userNameToUse,
          type,
          sendType === "notify" ? notifyTarget : null
        );
      }
      setMessage("");
      setSendType("chat");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please select a valid image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image must be smaller than 10MB');
      return;
    }

    setUploadError(null);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview({ file, previewUrl });
    e.target.value = '';
  };

  const handleImageCancel = () => {
    if (imagePreview?.previewUrl) {
      URL.revokeObjectURL(imagePreview.previewUrl);
    }
    setImagePreview(null);
    setUploadError(null);
  };

  const handleImageSend = async () => {
    if (!imagePreview) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const resizedFile = await setImageForResize(imagePreview.file);
      const attachment = await uploadChatImage("events", parentID, resizedFile);

      const userNameToUse = globalState?.user?.name || globalState?.vendor?.name || "Admin";
      const type = sendType === "notify" ? "notify" : null;

      const captionText = message.trim();
      if (typeof onSend === "function") {
        await onSend(captionText, currentUserID, userNameToUse, type, sendType === "notify" ? notifyTarget : null, [attachment]);
      } else {
        await EventAPI.sendMessage(
          parentID,
          subCollectionName,
          captionText,
          currentUserID,
          userNameToUse,
          type,
          sendType === "notify" ? notifyTarget : null,
          [attachment]
        );
      }
      setMessage("");
      handleImageCancel();
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLocationSend = async (latitude, longitude, label) => {
    setIsSendingLocation(true);
    const attachment = { type: 'location', url: '', latitude, longitude, label: label || null };
    const userNameToUse = globalState?.user?.name || globalState?.vendor?.name || "Admin";
    try {
      if (typeof onSend === "function") {
        await onSend("", currentUserID, userNameToUse, null, null, [attachment]);
      } else {
        await EventAPI.sendMessage(parentID, subCollectionName, "", currentUserID, userNameToUse, null, null, [attachment]);
      }
    } catch (error) {
      console.error("Error sending location:", error);
    } finally {
      setIsSendingLocation(false);
      setShowLocationPicker(false);
    }
  };

  const handleSoftDelete = async (messageId) => {
    try {
      await EventAPI.softDeleteMessage(parentID, subCollectionName, messageId);
    } catch (error) {
      console.error("Error deleting message:", error);
    }
    setReactionPickerMessageId(null);
  };

  const handlePermanentDelete = async (messageId) => {
    const confirmed = window.confirm(
      "Permanently delete this message? This cannot be undone and the message will disappear for everyone."
    );
    if (!confirmed) return;
    try {
      await EventAPI.permanentlyDeleteMessage(parentID, subCollectionName, messageId);
    } catch (error) {
      console.error("Error permanently deleting message:", error);
    }
    setReactionPickerMessageId(null);
  };

  const handleReaction = async (messageId, emoji) => {
    if (!currentUserID) return;
    const msg = messages.find(m => m.id === messageId);
    const hasReacted = msg?.reactions?.[emoji]?.includes(currentUserID);

    try {
      if (hasReacted) {
        await EventAPI.removeReaction(parentID, subCollectionName, messageId, emoji, currentUserID);
      } else {
        await EventAPI.addReaction(parentID, subCollectionName, messageId, emoji, currentUserID);
      }
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
    setReactionPickerMessageId(null);
  };

  useEffect(() => {
    setCurrentUserID(globalState?.vendor?.vendorID || globalState?.user?.userID)
  }, [globalState?.user, globalState?.vendor])

  const autoResizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    autoResizeTextarea();
  }, [message]);


  return (
    <div className="flex flex-col ">
      <div className="text-lg font-bold mb-2">{title}</div>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-grow overflow-y-auto overscroll-contain max-h-96 p-2 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg mb-2"
      >
        {loadingMore && (
          <div className="flex justify-center py-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          </div>
        )}
        {messages.length === 0 && !isInitialLoad ? (
          <div className="text-slate-400 dark:text-slate-500 text-center">No messages</div>
        ) : (
          messages.map((chat) => (
            <ChatMessage
              key={chat.id}
              chat={chat}
              currentUserID={currentUserID}
              reactionPickerMessageId={reactionPickerMessageId}
              setReactionPickerMessageId={setReactionPickerMessageId}
              REACTION_EMOJIS={REACTION_EMOJIS}
              handleReaction={handleReaction}
              handleSoftDelete={handleSoftDelete}
              handlePermanentDelete={handlePermanentDelete}
              setViewingImage={setViewingImage}
            />
          ))
        )}
        {/* end of messages sentinel removed; using containerRef.scrollTo */}
      </div>
      {showInput && (
        <div className="flex flex-col">
          <div className="flex overflow-x-auto no-scrollbar gap-2 mb-2 items-center whitespace-nowrap pb-1">
            <div
              onClick={() => setSendType("chat")}
              className={`ml-auto px-3 py-1 rounded-full text-xs cursor-pointer border flex-shrink-0 transition-colors ${sendType === "chat"
                ? "bg-primary text-white border-primary"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
            >
              silent
            </div>
            <div
              onClick={() => {
                setSendType("notify");
                setNotifyTarget(isPrivate ? "approved" : requireCheckIn ? "checked-in" : "everyone");
              }}
              className={`px-3 py-1 rounded-full text-xs cursor-pointer border flex-shrink-0 transition-colors ${sendType === "notify"
                ? "bg-primary text-white border-primary"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
            >
              notify
            </div>

            {sendType === "notify" && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-gray-300 dark:border-gray-600 flex-shrink-0">
                {isPrivate && (
                  <div
                    onClick={() => setNotifyTarget("approved")}
                    className={`px-3 py-1 rounded-full text-xs cursor-pointer border flex-shrink-0 transition-colors ${notifyTarget === "approved"
                      ? "bg-primary text-white border-primary"
                      : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                  >
                    approved
                  </div>
                )}
                {requireCheckIn && (
                <div
                  onClick={() => setNotifyTarget("checked-in")}
                  className={`px-3 py-1 rounded-full text-xs cursor-pointer border flex-shrink-0 transition-colors ${notifyTarget === "checked-in"
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                  checked-in
                </div>
                )}
                <div
                  onClick={() => setNotifyTarget("everyone")}
                  className={`px-3 py-1 rounded-full text-xs cursor-pointer border flex-shrink-0 transition-colors ${notifyTarget === "everyone"
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                  everyone
                </div>

              </div>
            )}
          </div>

          {imagePreview && (
            <div className="mb-2 p-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="relative inline-block">
                <img
                  src={imagePreview.previewUrl}
                  alt="Preview"
                  className="max-w-[200px] max-h-[200px] rounded-lg object-cover"
                />
                <button
                  onClick={handleImageCancel}
                  disabled={isUploading}
                  className="absolute -top-2 -right-2 bg-white dark:bg-gray-800 rounded-full p-0.5 shadow border border-gray-200 dark:border-gray-700 disabled:opacity-50"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
            </div>
          )}

          <div className="flex items-end">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleImageSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || imagePreview}
              className={`text-gray-500 dark:text-gray-400 hover:text-primary disabled:opacity-50 flex-shrink-0 overflow-hidden transition-all duration-200 ease-in-out ${message.trim() ? 'max-w-0 opacity-0 p-0 mx-0' : 'max-w-[40px] opacity-100 p-2 mr-1'}`}
            >
              <PhotoIcon className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              disabled={isUploading || isSendingLocation || !!imagePreview}
              className={`text-gray-500 dark:text-gray-400 hover:text-primary disabled:opacity-50 flex-shrink-0 overflow-hidden transition-all duration-200 ease-in-out ${message.trim() ? 'max-w-0 opacity-0 p-0 mx-0' : 'max-w-[40px] opacity-100 p-2 mr-1'}`}
            >
              <MapPinIcon className="w-6 h-6" />
            </button>
            <textarea
              ref={textareaRef}
              rows={1}
              placeholder={imagePreview ? "Add a message (optional)..." : "Type your message..."}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  imagePreview ? handleImageSend() : handleSendMessage();
                }
              }}
              className="flex-grow p-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 rounded-lg mr-2 resize-none overflow-hidden"
              style={{ minHeight: "38px", maxHeight: "160px", overflowY: "auto" }}
              disabled={isUploading}
            />
            <button
              onClick={imagePreview ? handleImageSend : handleSendMessage}
              className="px-4 py-2 bg-primary text-white rounded-lg disabled:opacity-50 flex-shrink-0"
              disabled={isUploading || isSending}
            >
              {isUploading || isSending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                'Send'
              )}
            </button>
          </div>
          {uploadError && <div className="text-red-500 dark:text-red-400 text-xs mt-1">{uploadError}</div>}
        </div>
      )}

      {/* Location picker modal */}
      {showLocationPicker && (
        <LocationPickerModal
          onShare={handleLocationSend}
          onClose={() => setShowLocationPicker(false)}
          isSending={isSendingLocation}
        />
      )}

      {/* Full-size image viewer */}
      {viewingImage && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center" onClick={() => setViewingImage(null)}>
          <div className="relative max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <button
              className="absolute -top-3 -right-3 bg-white dark:bg-gray-800 rounded-full p-1 shadow-lg z-10"
              onClick={() => setViewingImage(null)}
            >
              <XMarkIcon className="w-5 h-5 text-gray-700 dark:text-gray-200" />
            </button>
            <img
              src={viewingImage.url}
              alt="Full size"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};
